import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PasswordProtectedError } from '../../models/errors.model';
import {
  CopyDocumentsResponse,
  CopyDocumentsResponseSchema,
  CreateDocumentRequest,
  DocumentLogResponse,
  DocumentLogResponseArraySchema,
  DocumentProcessingStatusResponse,
  DocumentPurpose,
  DocumentResponse,
  DocumentSource,
  DocumentStatus,
  DocumentsWithAcknowledgementArraySchema,
  DocumentType,
  DocumentWithAcknowledgement,
  EvidenceSessionResponse,
  EvidenceUploadResponse,
  GetDocumentByIdResponse,
  GetDocumentStatusResponse,
  MultipleUploadResponse,
  MultipleUploadResponseSchema,
  PreviewDocumentResponse,
  PreviewDocumentResponseSchema,
} from '../../models/document.model';
import { DEFAULT_RETRY_POLICY } from '../../utils/http-request/http-retry-policy';
import { PolicyWrapperService } from '../http-retry/policy-wrapper.service';
import { SmartrService } from '../smartr.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentService extends SmartrService {
  private readonly http = inject(HttpClient);
  private readonly policy = inject(PolicyWrapperService);

  /**
   * Uploads a document to the Evidence API for processing
   * The backend handles AI classification, polling, and approval
   * @param clientIds - All applicants on the case the document should be visible to.
   *   For a joint application this is every client; for a single applicant it is just
   *   the uploading client. Defaults to [clientId] when not supplied.
   * @returns Observable with sessionId for status polling
   */
  uploadDocument(
    file: File,
    clientId: number,
    caseId: number,
    brokerageId: string,
    clientName: string,
    clientEmail: string,
    clientIds?: number[]
  ): Observable<{ sessionId: string }> {
    if (!caseId || caseId <= 0) {
      return throwError(() => new Error(`Invalid case ID: ${caseId}. Case ID must be a positive integer.`));
    }

    const sessionId = crypto.randomUUID();
    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('file', file);
    formData.append('clientId', clientId.toString());
    formData.append('caseId', caseId.toString());
    formData.append('clientName', clientName);
    formData.append('clientEmail', clientEmail);

    // Tag the document with every applicant on the case so joint applicants both
    // see it. Falls back to the uploading client when no list is supplied. Sent as
    // repeated same-name `clientIds` keys, which ASP.NET binds (case-insensitively)
    // to List<int>; the server dedupes and drops non-positive ids.
    const resolvedClientIds = clientIds && clientIds.length > 0 ? clientIds : [clientId];
    resolvedClientIds.forEach((id) => {
      formData.append('clientIds', id.toString());
    });

    const url = environment.evidence.upload;

    return this.policy
      .For(() => this.http.post<EvidenceUploadResponse>(url, formData, {
        headers: new HttpHeaders({
          'smt-target-brokerage': brokerageId,
        }),
      }), {
        retryPolicy: DEFAULT_RETRY_POLICY,
      })
      .pipe(
        map(() => ({ sessionId })),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 422) {
            return throwError(() => new PasswordProtectedError(
              error.error?.detail ?? 'File is password-protected'
            ));
          }
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          return throwError(() => new Error(`Document upload failed: ${errorMessage}`));
        })
      );
  }

  /**
   * Fires a fire-and-forget notification to SmartrWeb that a document was uploaded.
   * POST /client/{clientId}/notifications/document-uploaded
   * @param clientId - The client ID (resolved into the URL template)
   * @param caseId - The case ID the upload belongs to
   */
  notifyUpload(clientId: number, caseId: number): Observable<void> {
    const url = environment.notifyDocumentUploaded.replace('{{clientId}}', String(clientId));
    return this.http.post<void>(url, { caseId });
  }

  /**
   * Gets the processing status of a document upload session
   * GET /api/evidence/sessions/{sessionId}
   */
  getProcessingStatus(sessionId: string, brokerageId: string): Observable<DocumentProcessingStatusResponse> {
    const url = `${environment.evidence.sessionStatus}/${sessionId}`;

    return this.http.get<EvidenceSessionResponse>(url, {
      headers: new HttpHeaders({
        'smt-target-brokerage': brokerageId,
      }),
    }).pipe(
      map((response): DocumentProcessingStatusResponse => ({
        sessionId: response.data.sessionId,
        status: response.data.status,
        documentIds: response.data.documents
          ?.map(doc => doc.documentsApiDocumentId)
          .filter((id): id is string => id != null),
        createdAt: response.timestamp,
        updatedAt: new Date().toISOString(),
      })),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return throwError(() => new Error('Session not found'));
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Validates file size against the maximum allowed size
   * @param file - The file to validate
   * @returns true if file size is within limits
   * @throws Error if file is too large
   */
  validateFileSize(file: File): boolean {
    const MAX_SIZE = 10 * 1024 * 1024;

    if (file.size > MAX_SIZE) {
      throw new Error(
        `File size (${file.size} bytes) exceeds maximum allowed size (${MAX_SIZE} bytes)`
      );
    }

    return true;
  }
  /**
   * Retrieves documents with required filtering
   * GET /api/Documents?clientIds={clientId}
   * @param clientId - Required client ID
   * @param caseId - Required case ID
   * @returns Observable that emits array of documents
   */
  getDocuments(
    clientId: number,
    caseId: number,
    brokerageId: string
  ): Observable<DocumentResponse[]> {
    const params = new HttpParams().set('clientIds', clientId.toString());
    const headers = new HttpHeaders({
      'smt-target-brokerage': brokerageId,
    });
    const url = `${this.getDocumentsUrl()}/Documents`;

    return this.http.get<DocumentWithAcknowledgement[]>(url, { params, headers }).pipe(
      this.verifyResponse(DocumentsWithAcknowledgementArraySchema),
      map((documents) => {
        return documents
          .filter((doc) => doc.caseId === caseId || doc.caseId == null)
          .map((doc): DocumentResponse => {
            const clientAcknowledgement = doc.acknowledgements.find(
              (ack) => ack.clientId === clientId
            );
            const acknowledged = clientAcknowledgement?.acknowledged ?? false;

            // Use legacyDocumentId if available (for migrated documents), otherwise use UUID
            const documentId: number | string =
              doc.legacyDocumentId && doc.legacyDocumentId > 0
                ? doc.legacyDocumentId
                : (doc.documentId ?? '');

            return {
              DocumentId: documentId,
              CreatedBy: 0, // Not available in Documents API response
              DateCreated: doc.dateCreated,
              DocumentType: doc.documentType,
              DocumentSource: doc.documentSource,
              DocumentPurposes: [doc.documentPurpose],
              ClientIds: doc.clientIds,
              ClientId: doc.clientIds[0] ?? clientId,
              ProductId: doc.caseId,
              IsDeleted: doc.isDeleted,
              ContentType: doc.contentType,
              FileName: doc.fileName,
              LastUpdated: doc.lastUpdated ?? doc.dateCreated,
              IsVisible: doc.isVisible,
              NeedsAcknowledgement: doc.needsAcknowledgement,
              Acknowledged: acknowledged,
              IsExpired: doc.isExpired,
              VerificationIds: [], // Not available in Documents API response
              Products: [], // Not available in Documents API response
              ClientFullNames: '', // Not available in Documents API response
              DocumentUuid: doc.documentId ?? undefined,
              IsLegacy: doc.isLegacy,
            };
          });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Retrieves document logs with required filtering
   * GET /documents/DocumentLogs?clientId={clientId}&caseId={caseId}
   * @param clientId - Required client ID
   * @param caseId - Required case ID
   * @returns Observable that emits array of document logs
   */
  getDocumentLogs(clientId: number, caseId: number): Observable<DocumentLogResponse[]> {
    const params = new HttpParams()
      .set('clientId', clientId.toString())
      .set('caseId', caseId.toString());

    const url = `${this.getDocumentsUrl()}/DocumentLogs`;

    return this.http.get<DocumentLogResponse[]>(url, { params }).pipe(
      this.verifyResponse(DocumentLogResponseArraySchema),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  // TODO: Wave 4B - Placeholder GET operations for document retrieval

  /**
   * Retrieves a document by its ID with full metadata
   * TODO: Backend team needs to implement GET /api/v2/document/{id} endpoint
   * TODO: Confirm the actual response structure and URL pattern
   * TODO: Determine if authentication is required for this endpoint
   *
   * @param id - The document ID to retrieve
   * @returns Observable that emits document metadata and details
   */
  getDocumentById(id: number): Observable<GetDocumentByIdResponse> {
    // TODO: Replace with actual HTTP GET call when endpoint is available
    // Example: return this.http.get<GetDocumentByIdResponse>(`${this.getDocumentByIdUrl()}/${id}`)
    //   .pipe(
    //     this.verifyResponse(GetDocumentByIdResponseSchema),
    //     catchError((error: HttpErrorResponse) => throwError(() => error))
    //   );

    const mockResponse: GetDocumentByIdResponse = {
      documentId: id,
      fileName: `document-${id}.pdf`,
      contentType: 'application/pdf',
      documentType: DocumentType.FactFind,
      documentPurpose: DocumentPurpose.FinancialDocuments,
      documentSource: DocumentSource.Client,
      organisationId: 'placeholder-org-id',
      uploadedAt: new Date().toISOString(),
      uploadedByEmail: 'user@example.com',
      uploadedByName: 'Mock User',
      fileSize: 1024000,
      status: DocumentStatus.Completed,
      clientDocuments: [{ ClientId: 123, ProductId: 456 }],
    };

    return new Observable((observer) => {
      observer.next(mockResponse);
      observer.complete();
    });
  }

  /**
   * Retrieves the processing status of a document
   * TODO: Backend team needs to implement GET /api/v2/document/{id}/status endpoint
   * TODO: Confirm the actual response structure for status tracking
   * TODO: Define the complete processing pipeline stages
   *
   * @param id - The document ID to check status for
   * @returns Observable that emits current document processing status
   */
  getDocumentStatus(id: number): Observable<GetDocumentStatusResponse> {
    // TODO: Replace with actual HTTP GET call when endpoint is available
    // Example: return this.http.get<GetDocumentStatusResponse>(`${this.getDocumentStatusUrl()}/${id}`)
    //   .pipe(
    //     this.verifyResponse(GetDocumentStatusResponseSchema),
    //     catchError((error: HttpErrorResponse) => throwError(() => error))
    //   );

    const mockResponse: GetDocumentStatusResponse = {
      documentId: id,
      status: DocumentStatus.Processing,
      statusUpdatedAt: new Date().toISOString(),
      processingSteps: [
        {
          step: 'Upload Complete',
          status: DocumentStatus.Completed,
          completedAt: new Date(Date.now() - 300000).toISOString(), // 5 minutes ago
        },
        {
          step: 'Virus Scan',
          status: DocumentStatus.Completed,
          completedAt: new Date(Date.now() - 240000).toISOString(), // 4 minutes ago
        },
        {
          step: 'Document Analysis',
          status: DocumentStatus.Processing,
        },
      ],
    };

    return new Observable((observer) => {
      observer.next(mockResponse);
      observer.complete();
    });
  }

  /**
   * Downloads a document by its ID (legacy SmartrWeb endpoint)
   * GET /smartrweb/client/{clientId}/documents/{documentId}/{clientId}
   * @param clientId - Client ID
   * @param documentId - Document ID to download
   * @returns Observable that emits a Blob for download
   */
  downloadDocument(clientId: number, documentId: number): Observable<Blob> {
    const url = environment.documents
      .replace(/\{\{clientId\}\}/g, clientId.toString())
      .replace(/\{\{documentId\}\}/g, documentId.toString());

    return this.http
      .get<Blob>(url, {
        responseType: 'blob' as 'json', // Cast to satisfy TypeScript
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Downloads a document by its UUID (Documents API endpoint)
   * GET /documents/Documents/download?documentIds={documentId}
   * @param documentId - Document UUID to download
   * @param brokerageId - Brokerage ID for the smt-target-brokerage header
   * @returns Observable that emits a Blob for download
   */
  downloadDocumentByUuid(documentId: string, brokerageId: string): Observable<Blob> {
    const params = new HttpParams().set('documentIds', documentId);
    const headers = new HttpHeaders({
      'smt-target-brokerage': brokerageId,
    });
    const url = `${this.getDocumentsUrl()}/Documents/download`;

    return this.http
      .get<Blob>(url, {
        params,
        headers,
        responseType: 'blob' as 'json', // Cast to satisfy TypeScript
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Gets the base documents URL from environment configuration
   * @returns The base documents endpoint URL
   */
  private getDocumentsUrl(): string {
    return environment.documentStore.baseUrl;
  }

  /**
   * Gets a preview URL for a document
   * GET /documents/Documents/{documentId}/preview
   * @param documentId - The document ID to preview
   * @returns Observable that emits preview information
   */
  previewDocument(documentId: string): Observable<PreviewDocumentResponse> {
    const url = `${this.getDocumentsUrl()}/Documents/${documentId}/preview`;

    return this.http.get<PreviewDocumentResponse>(url).pipe(
      this.verifyResponse(PreviewDocumentResponseSchema),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Uploads multiple documents
   * POST /documents/Documents/multiple
   * @param files - Array of files to upload
   * @param request - Base document request (without Document field)
   * @returns Observable that emits upload results
   */
  uploadMultipleDocuments(
    files: File[],
    request: Omit<CreateDocumentRequest, 'Document'>
  ): Observable<MultipleUploadResponse> {
    const formData = new FormData();

    // Append all files
    files.forEach((file, index) => {
      formData.append(`Documents[${index}]`, file, file.name);
    });

    // Append ClientIds as individual array items
    request.ClientDocuments.forEach((clientDoc, index) => {
      formData.append(`ClientIds[${index}]`, clientDoc.ClientId.toString());
    });

    // Append optional CaseId
    const productId = request.ClientDocuments[0]?.ProductId;
    if (productId !== undefined) {
      formData.append('CaseId', productId.toString());
    }

    // Append enum values
    formData.append('DocumentType', request.DocumentType.toString());
    formData.append('DocumentPurpose', request.DocumentPurpose.toString());
    formData.append('DocumentSource', request.DocumentSource.toString());

    // Append required string fields
    formData.append('OrganisationId', request.OrganisationId);

    // Append optional string fields
    if (request.CreatedByEmail) {
      formData.append('CreatedByEmail', request.CreatedByEmail);
    }
    if (request.CreatedByName) {
      formData.append('CreatedByName', request.CreatedByName);
    }

    const url = `${this.getDocumentsUrl()}/Documents/multiple`;

    return this.http.post<MultipleUploadResponse>(url, formData).pipe(
      this.verifyResponse(MultipleUploadResponseSchema),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Copies documents to a different case
   * POST /documents/Documents/copy/case/{caseId}
   * @param caseId - The target case ID
   * @param documentIds - Array of document IDs to copy
   * @returns Observable that emits copy results
   */
  copyDocumentsToCase(caseId: number, documentIds: string[]): Observable<CopyDocumentsResponse> {
    const url = `${this.getDocumentsUrl()}/Documents/copy/case/${caseId}`;

    return this.http.post<CopyDocumentsResponse>(url, { documentIds }).pipe(
      this.verifyResponse(CopyDocumentsResponseSchema),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Acknowledges a document (Documents API endpoint)
   * POST /documents/Documents/acknowledge
   * @param documentId - The document UUID to acknowledge
   * @param clientId - The client ID acknowledging the document
   * @param brokerageId - The brokerage ID for the smt-target-brokerage header
   * @returns Observable that emits acknowledgement result
   */
  acknowledgeDocument(
    documentId: string,
    clientId: number,
    brokerageId: string
  ): Observable<{ acknowledged: boolean }> {
    const url = `${this.getDocumentsUrl()}/Documents/acknowledge`;
    const headers = new HttpHeaders({
      'smt-target-brokerage': brokerageId,
    });

    const payload = {
      documentId: documentId,
      clientId: clientId,
      acknowledged: true,
      leadId: null,
      adviserId: null,
      financeType: 'Mortgages', // Default value, could be parameterized if needed
    };

    return this.http.post(url, payload, { headers, observe: 'response' }).pipe(
      map((response) => {
        // API returns 204 No Content on success
        return { acknowledged: response.status === 204 };
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }
  /**
   * Deletes a document by its UUID (Documents API endpoint)
   * DELETE /documents/Documents/{documentId}
   * @param documentId - Document UUID to delete
   * @param brokerageId - Brokerage ID for the smt-target-brokerage header
   * @returns Observable that emits deletion result
   */
  deleteDocument(documentId: string, brokerageId: string): Observable<void> {
    const headers = new HttpHeaders({
      'smt-target-brokerage': brokerageId,
    });
    const url = `${this.getDocumentsUrl()}/Documents/${documentId}`;

    return this.http.delete<void>(url, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Deletes a legacy document using SmartWeb API
   * DELETE /smartrweb/client/{clientId}/documents?documentid={legacyDocumentId}
   * @param clientId - Client ID
   * @param legacyDocumentId - Legacy document ID to delete
   * @returns Observable that emits deletion result
   */
  deleteLegacyDocument(clientId: number, legacyDocumentId: number): Observable<void> {
    // Use the dedicated documentsDelete endpoint from environment
    const url = environment.documentsDelete.replace('{{clientId}}', clientId.toString());
    const params = new HttpParams().set('documentid', legacyDocumentId.toString());

    return this.http.delete<void>(url, { params }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Retrieves documents requiring acknowledgement for a specific client
   * GET /documents/Documents?clientIds={clientId}
   * @param clientId - The client ID to get documents for
   * @param brokerageId - The brokerage ID for the smt-target-brokerage header
   * @returns Observable that emits array of documents requiring acknowledgement
   */
  getDocumentsRequiringAcknowledgement(
    clientId: number,
    brokerageId: string
  ): Observable<DocumentWithAcknowledgement[]> {
    const params = new HttpParams().set('clientIds', clientId.toString());
    const headers = new HttpHeaders({
      'smt-target-brokerage': brokerageId,
    });
    const url = `${this.getDocumentsUrl()}/Documents`;

    return this.http.get<DocumentWithAcknowledgement[]>(url, { params, headers }).pipe(
      this.verifyResponse(DocumentsWithAcknowledgementArraySchema),
      map((documents) => {
        const documentsSanitised = documents.map((doc) => ({
          ...doc,
          documentId: doc.documentId ?? '',
        }));

        // Filter documents where needsAcknowledgement is true and client has not acknowledged
        return documentsSanitised.filter((doc) => {
          if (!doc.needsAcknowledgement) {
            return false;
          }

          const clientAcknowledgement = doc.acknowledgements.find(
            (ack) => ack.clientId === clientId
          );

          return !clientAcknowledgement || !clientAcknowledgement.acknowledged;
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Retrieves documents with required filtering (Promise version)
   * @param clientId - Required client ID
   * @param caseId - Required case ID
   * @param brokerageId - Brokerage ID
   * @returns Promise that resolves with array of documents
   */
  async getDocumentsAsync(
    clientId: number,
    caseId: number,
    brokerageId: string
  ): Promise<DocumentResponse[]> {
    return this.getDocuments(clientId, caseId, brokerageId).toPromise() as Promise<
      DocumentResponse[]
    >;
  }

  /**
   * Uploads a document (Promise version)
   * @param file - File to upload
   * @param clientId - Client ID
   * @param caseId - Case ID
   * @param brokerageId - Brokerage ID
   * @param clientName - Client name
   * @param clientEmail - Client email
   * @returns Promise that resolves with sessionId for status polling
   */
  async uploadDocumentAsync(
    file: File,
    clientId: number,
    caseId: number,
    brokerageId: string,
    clientName: string,
    clientEmail: string,
    clientIds?: number[]
  ): Promise<{ sessionId: string }> {
    return this.uploadDocument(
      file,
      clientId,
      caseId,
      brokerageId,
      clientName,
      clientEmail,
      clientIds
    ).toPromise() as Promise<{ sessionId: string }>;
  }

  /**
   * Deletes a document by its UUID (Promise version)
   * @param documentId - Document UUID to delete
   * @param brokerageId - Brokerage ID
   * @returns Promise that resolves when deletion is complete
   */
  async deleteDocumentAsync(documentId: string, brokerageId: string): Promise<void> {
    return this.deleteDocument(documentId, brokerageId).toPromise() as Promise<void>;
  }

  /**
   * Deletes a legacy document (Promise version)
   * @param clientId - Client ID
   * @param legacyDocumentId - Legacy document ID
   * @returns Promise that resolves when deletion is complete
   */
  async deleteLegacyDocumentAsync(clientId: number, legacyDocumentId: number): Promise<void> {
    return this.deleteLegacyDocument(clientId, legacyDocumentId).toPromise() as Promise<void>;
  }
}
